import React, { useEffect, useMemo, useState } from "react";
import Container from "../../components/ui/ui kit/Container";
import Input from "../../components/ui/ui kit/Input";
import Button from "../../components/ui/ui kit/Button";
import HintTooltip from "../../components/ui/HintTooltip";
import FlowStepper from "../../components/ui/FlowStepper";
import { ReactComponent as NextIcon } from "../../assets/icons/home/createLei/next.svg";
import { ReactComponent as CancelIcon } from "../../assets/icons/home/createLei/cancel.svg";
import {
  RenewLeiPage,
  Title,
  TabName,
  Hr,
  NavLink,
} from "./style";
import { useAuth } from "../../hooks/useAuth";
import { useAlert } from "../../hooks/useAlert";
import { useNavigate } from "react-router-dom";
import cl from "./RenewLei.module.sass";
import { useDispatch } from "react-redux";
import { fetchDataForm } from "../../redux/action-creators/dataFormReducer";
import Loader from "../../components/ui/Loader";
// Import Prices
import { calculateMultiYearPrice } from '../../components/functional/Prices/multiYearPrice';
import {
  calculateEntityTypeSurcharge,
  calculateLevel2Surcharge
} from '../../components/functional/Prices/surchargeUtils.js';
import { Helmet } from "react-helmet";
import { CreateLeiService } from "../../api/services/create-lei";
import {
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { muiTheme } from "../../theme";

interface JurisdictionOption {
  value: string;
  label: string;
  surcharge: number;
  requiresState?: boolean;
}

const defaultCompanyList = [{
  id: "",
  company_name: "",
  company_number: "",
  lei_number: "",
  entity_type: "",
  reporting_exception_reason: "",
  is_level_2_data_available: "",
  legal_jurisdiction: false,
  sa_first_name: "",
  sa_last_name: "",
  sa_email: "",
  sa_phone: "",
  next_renewal_date: "",
  registration_status: "",
  can_be_renewed: false,
}];

const defaultCompanyData = {
  company_name: "",
  company_number: "",
  lei_number: "",
  entity_type: "",
  reporting_exception_reason: "",
  is_level_2_data_available: false,
  legal_jurisdiction: "",
  sa_first_name: "",
  sa_last_name: "",
  sa_email: "",
  sa_phone: "",
  next_renewal_date: "",
  registration_status: "",
  can_be_renewed: false,
  multi_year_support: 0,
};

export const RenewLei = () => {
  const isAuth = useAuth();
  const showAlert = useAlert();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [loading, setLoading] = useState<boolean>(false);

  // Jurisdictions - fetched live from RapidLEI (via the backend), not a
  // static bundled list, purely for looking up the surcharge that applies
  // to each renewal candidate's existing jurisdiction (there's no country
  // picker on this page - the jurisdiction comes from the LEI being renewed).
  const [jurisdictionCountries, setJurisdictionCountries] = useState<JurisdictionOption[]>([]);
  const [jurisdictionStates, setJurisdictionStates] = useState<Record<string, JurisdictionOption[]>>({});

  // Company data stored in Session
  const [multiYearSupport, setMultiYearSupport] = useState(1);
  const [surchargePrice, setSurchargePrice] = useState(0);
  const [price, setPrice] = useState(0);

  const [nextButtonEnabled, setNextButtonEnabled] = useState<boolean>(false);
  const [companyData, setCompanyData] = useState(defaultCompanyData);
  const [companyList, setCompanyList] = useState(defaultCompanyList);
  const [isLevel1DataSame, setIsLevel1DataSame] = useState(true);
  const [userComments, setUserComments] = React.useState("");

  const navigate = useNavigate();

  const isLevel2DataAvailable = companyData && companyData.lei_number && companyData.is_level_2_data_available;

  let multiYearPrice = calculateMultiYearPrice(multiYearSupport);
  let entityTypeSurcharge = calculateEntityTypeSurcharge(
    companyData && companyData.entity_type ? companyData.entity_type : "GENERAL"
  );
  let level2Surcharge = calculateLevel2Surcharge(isLevel2DataAvailable);
  const countryCode = companyData ? companyData.legal_jurisdiction : "";

  useEffect(() => {
    const getCompanyList = async () => {
      try {
        setCompanyList([]);
        setCompanyData(defaultCompanyData);
        setLoading(true);
        const res = await CreateLeiService.requestLeiList();

        const data = res.data.map((item: any) => ({
          id: item.lei_number,
          ...item,
          // can_be_renewed: true,
        }));
  
        setCompanyList(data);
      } catch (error) {
        console.log('-> ERROR in getCompanyList:', error);
      } finally {
        setLoading(false);
      }
    }
    
    getCompanyList();
  }, []);

  //Scroll to top
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const requestCountries = async () => {
      try {
        const res = await CreateLeiService.requestCountries();
        const data = res.data.data;

        setJurisdictionCountries(
          data.countries.map((c: any): JurisdictionOption => ({
            value: c.code,
            label: `${c.label} (${c.code})`,
            surcharge: c.surcharge,
            requiresState: c.requires_state,
          }))
        );

        const statesMap: Record<string, JurisdictionOption[]> = {};
        Object.entries(data.states).forEach(([prefix, options]) => {
          statesMap[prefix] = (options as any[]).map((s: any): JurisdictionOption => ({
            value: s.code,
            label: `${s.label} (${s.code})`,
            surcharge: s.surcharge,
          }));
        });
        setJurisdictionStates(statesMap);
      } catch (error) {
        console.log(error);
      }
    };

    requestCountries();
  }, []);

  useEffect(() => {
    let updatedPrice: number
    let updatedSurchargePrice: number

    let selectedCountryCode = "";

    if (countryCode) {
      selectedCountryCode = countryCode.split("-")[0];
    }

    const countryMeta = jurisdictionCountries.find(country => country.value === selectedCountryCode);

    if (countryMeta?.requiresState) {
      const selectedState = (jurisdictionStates[selectedCountryCode] || []).find(state => state.value === countryCode);
      updatedPrice = selectedState
        ? multiYearSupport * (selectedState.surcharge + multiYearPrice + entityTypeSurcharge + level2Surcharge)
        : 0;
      updatedSurchargePrice = selectedState ? multiYearSupport * selectedState.surcharge: 0;
      setSurchargePrice(updatedSurchargePrice);
      setPrice(updatedPrice);
    } else {
      updatedPrice = countryMeta
        ? multiYearSupport * (countryMeta.surcharge + multiYearPrice + entityTypeSurcharge + level2Surcharge)
        : 0;
      updatedSurchargePrice = countryMeta
        ? multiYearSupport * countryMeta.surcharge
        : 0;
      setSurchargePrice(updatedSurchargePrice);
      setPrice(updatedPrice);
    }

    console.log('---> price:', updatedPrice);

    setCompanyData(companyData => ({
      ...companyData,
      multi_year_support: multiYearSupport,
      price: updatedPrice,
      multi_year_price: multiYearPrice,
      entity_type_surcharge: entityTypeSurcharge,
      level2_surcharge: level2Surcharge,
      surcharge_price: updatedSurchargePrice,
      is_level_1_data_same: isLevel1DataSame,
      user_comment: isLevel1DataSame ? '' : userComments.trim()
    }));
  }, [
    countryCode, entityTypeSurcharge, isLevel1DataSame, level2Surcharge,
    multiYearPrice, multiYearSupport, userComments, jurisdictionCountries, jurisdictionStates,
  ]);

  useEffect(() => {
    if (
      "company_name" in companyData
      && "lei_number" in companyData
      && "multi_year_support" in companyData
    ) {
      if (
        companyData["company_name"]
        && companyData["lei_number"]
        && companyData["multi_year_support"]
        && (isLevel1DataSame || userComments.trim())
      ) {
        setNextButtonEnabled(true);
        console.log("true");
      } else {
        setNextButtonEnabled(false);
      }
    } else {
      setNextButtonEnabled(false);
    }
    console.log("companyData", companyData)
  }, [companyData, isLevel1DataSame, userComments]);

  const checkCompany = async (row: any) => {
    const res = await CreateLeiService.orderSubmitStatus({
      order_type: 'Transfer',
      company_name: row['company_name'],
      company_number: row['company_number'],
      legal_jurisdiction: row['legal_jurisdiction']
    })

    const submitStatus = res.data.data['status'];
    if (submitStatus !== 'NO_ACTIVE_ORDERS') {
      const existingCompanyName = row["company_name"];
      const alertMessage = submitStatus === 'ALREADY_REGISTERED'
        ? `LEI already renews for company '${existingCompanyName}', please select another company.`
        : `There are active orders for company '${existingCompanyName}', please select another company.`;;      
      showAlert(alertMessage);
      setCompanyData(defaultCompanyData);
      return;
    } else {
      const data = {
        ...row,
        multi_year_support: multiYearSupport,
        is_level_1_data_same: isLevel1DataSame,
      };
      setCompanyData(data);
      setCompanyList([{
        ...row,
        id: row.id + "s",
      }]);
      // The list collapses down to just the selected row and the rest of
      // the form appears below it - if the user picked a company from the
      // bottom of a long list, they'd otherwise stay scrolled down with no
      // indication anything happened.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const formatRenewalDate = (date: string) => date ? date.slice(0, 19).replace("T", " ") : "n/a";

  const renderRenewButton = (row: any) => {
    let isRowSelected = false;
    if (companyData && companyData['company_name'] && companyData['lei_number']) {
      isRowSelected = companyData['company_name'] === row['company_name'] &&
        companyData['lei_number'] === row['lei_number'];
    }

    if (!row['can_be_renewed']) {
      return null;
    }

    return (
      <Button
        styleType="primary"
        onClick={() => checkCompany(row)}
        disabled={isRowSelected}
        width="80px"
        height="34px"
        noMarginTop
      >
        {isRowSelected ? 'Selected' : 'Renew'}
      </Button>
    );
  };

  const handleNext = async () => {
    const data = companyData;
    setLoading(true);
    const preparedData = {
      ...data,
      order_type: 'Renew'
    };
    console.log("data", preparedData);
    await dispatch(fetchDataForm(preparedData))

    navigate('/relationship',
    {
      // state: {
      //   companyName: companyData["company_name"],
      //   companyNumber: companyData["company_number"],
      //   entityType: entityType,
      //   countryCode: countryCode,
      //   multiYearPrice: multiYearPrice,
      //   multiYearSupport: companyData["multi_year_support"],
      //   surchargePrice: surchargePrice,
      //   entityTypeSurcharge: entityTypeSurcharge,
      //   level2Surcharge: level2Surcharge,
      //   price: price,
      // },
    });
  };

  const tableBottomText = useMemo(() => {
    if (loading) {
      return "";
    }

    const renewableCompanies = companyList.filter(item => item.can_be_renewed);

    if (renewableCompanies.length === 0) {
      return "Sorry no LEI records available for Renewal.";
    }

    if (companyData && companyData.lei_number) {
      return '';
    }

    return 'Please select a LEI for Renewal.'
  }, [companyData, companyList, loading]);

  return (
    <Container>
      <Helmet> {/* don't forget: import { Helmet } from "react-helmet"; */}
        <title>LEI Renewal – Entity data</title>
        <meta 
          name="description" 
          content="LEI Renew Process – Entity data. LEIpapa is an official LEI Registration Agent of Ubisecure RapidLEI. Press Create LEI Application to Register, Renew or Transfer an LEI." 
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {isAuth && (
        <RenewLeiPage>
          <div className={cl["container_big"]}>
            <FlowStepper activeStep={0} flowName="Renewal" />
            <div className={cl["title_wrap"]}>
              <Title>Entity data</Title>
            </div>
            <TabName className={cl["tab_name"]}>
              Please select an LEI for renewal
            </TabName>

            <div className={cl["card_body"]}>
              <div className={cl["table_wrap"]}>
                {loading ? <Loader /> : ""}
                {companyList.length > 0 && (
                  isMobile ? (
                    <Stack spacing={1.5}>
                      {companyList.map((row: any, index: number) => (
                        <Card key={row.id ?? index} variant="outlined">
                          <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {row.company_name}
                              </Typography>
                              <Chip
                                size="small"
                                label={row.registration_status || "n/a"}
                                color={row.registration_status === "ISSUED" ? "success" : "error"}
                              />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {row.lei_number}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {row.legal_jurisdiction} &middot; Next renewal: {formatRenewalDate(row.next_renewal_date)}
                            </Typography>
                            {row.can_be_renewed && (
                              <Stack sx={{ mt: 1.5 }}>
                                {renderRenewButton(row)}
                              </Stack>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>LEI</TableCell>
                            <TableCell>Legal Name</TableCell>
                            <TableCell align="center">Country</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Next renewal date</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {companyList.map((row: any, index: number) => (
                            <TableRow key={row.id ?? index} hover>
                              <TableCell>{row.lei_number}</TableCell>
                              <TableCell>{row.company_name}</TableCell>
                              <TableCell align="center">{row.legal_jurisdiction}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={row.registration_status || "n/a"}
                                  color={row.registration_status === "ISSUED" ? "success" : "error"}
                                />
                              </TableCell>
                              <TableCell align="center">{formatRenewalDate(row.next_renewal_date)}</TableCell>
                              <TableCell align="right">{renderRenewButton(row)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )
                )}
              </div>
              <div className={cl["wrap_l2"]}>
                <p style={{ lineHeight: "1.5" }}>
                  {tableBottomText}
                </p>
              </div>

              {companyData && companyData.lei_number && (
                <>
                  <div className={cl["wrap_l2"]}>
                    <p style={{ lineHeight: "1.5", fontWeight: 'bold' }}>
                      Did you have any changes in the structure of the Legal Entity (Level 1 data)?
                    </p>
                  </div>
                  <div className={cl['radio_wrapper']}>
                    <input
                      style={{ marginRight: '15px' }}
                      id="level1-same-no"
                      type="radio"
                      name="level1-same"
                      checked={isLevel1DataSame}
                      onChange={(e) => setIsLevel1DataSame(e.target.checked)}
                    />
                    <label htmlFor="level1-same-no">
                      No changes. Level 1 data remains the same.
                    </label>
                  </div>
                  <div className={cl['radio_wrapper']}>
                    <input
                      id="level1-same-yes"
                      style={{ marginRight: '15px' }}
                      type="radio"
                      name="level1-same"
                      checked={!isLevel1DataSame}
                      onChange={(e) => setIsLevel1DataSame(!e.target.checked)}
                    />
                    <label htmlFor="level1-same-yes">
                      Yes, Level 1 data has changed.
                    </label>
                  </div>
                  {!isLevel1DataSame && (
                    <div className={cl["input-comments-wrapper"]}>
                      <label htmlFor="userComment">
                        Please leave a comment on what's changed in the structure of the Legal Entity (Level 1 data).
                      </label>
                      <textarea 
                        className={cl["textarea_custom"]}
                        id="userComment"
                        placeholder="Max 400 characters"
                        maxLength={400}
                        value={userComments}
                        onChange={e => {
                          setUserComments(e.target.value || "")
                        }}
                      ></textarea>
                    </div>
                  )}

                  <div className={cl["input_wrap_years"]}>
                    <div>
                      <Input
                        type="select"
                        id="multiyearsupport"
                        placeholder="Year to support"
                        uiProps={{width: "100%", margin: "0px"}}
                        value={multiYearSupport}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setMultiYearSupport(Number(event.target.value))}
                        options={["1", "2", "3", "4", "5"]}
                      />
                    </div>
                  </div>

                  <Hr />
                  <div className={cl["wrap_l2_right"]}>
                    <div className={cl["price_text"]}>Order calculation</div>
                    <p>Years to support: {multiYearSupport}</p>
                    <p>Base price: €{multiYearPrice} x {multiYearSupport} = {multiYearPrice * multiYearSupport}</p>
                    {surchargePrice > 0 && (
                      <p>Jurisdictional surcharge: €{surchargePrice/multiYearSupport} x {multiYearSupport} = {surchargePrice}</p>
                    )}
                    {entityTypeSurcharge > 0 && (
                      <p>Entity type surcharge: €{entityTypeSurcharge} x {multiYearSupport} = {entityTypeSurcharge * multiYearSupport}</p>
                    )}
                    {level2Surcharge > 0 && (
                      <p>Level 2 surcharge: €{level2Surcharge} x {multiYearSupport} = {level2Surcharge * multiYearSupport}</p>
                    )}
                    <p>Total: €{price}</p>
                    {/* <p>Total: €{multiYearPrice * multiYearSupport + surchargePrice + entityTypeSurcharge + level2Surcharge}</p> */}
                    {/* <span className={cl["price_text"]}>Level 2 surcharge: €{level2Surcharge}</span> */}
                  </div>
                </>
              )}
            </div>
            <div className={cl["title_inner-button"]}>
              <Button
                styleType="danger"
                component={NavLink}
                to="/"
                startIcon={<CancelIcon />}
                width="auto"
                noMarginTop
              >
                Cancel
              </Button>

              <Button
                styleType="primary"
                onClick={handleNext}
                disabled={!nextButtonEnabled}
                startIcon={<NextIcon />}
                width="auto"
                noMarginTop
              >
                Next
              </Button>
            </div>
          </div>
        </RenewLeiPage>
      )}
    </Container>
  );
};
